import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

// ============================================
// Types
// ============================================

export interface CompanyRegistrationData {
  companyName: string;
  companyCnpj: string;
  companyType: string;
  tradeName: string;
  companyEmail: string;
  companyPhone: string;
  companyWebsite?: string;
  companyDescription: string;
  representativeName: string;
  representativeCpf: string;
  representativeDocType: string;
  representativeBirthDate: string;
  representativePhone: string;
  representativeMotherName: string;
  addressZipCode: string;
  addressStreet: string;
  addressNumber: string;
  addressNeighborhood: string;
  addressCity: string;
  addressState: string;
  addressComplement?: string;
  accountEmail: string;
  accountPassword: string;
  // Optional fields
  sellerName?: string;
  sacPhone?: string;
  averageMonthlyTurnover?: string;
  averageTicket?: string;
  typeProduct?: string;
  productsSold?: string;
  companyObjectives?: string;
  companyAge?: string;
}

export interface CompanyDocument {
  id: string;
  registrationId: string;
  documentType: string;
  fileUrl: string;
  fileHash: string;
  fileName: string;
  fileMimeType: string;
  fileSizeBytes: number;
  isReplaced: boolean;
  uploadedAt: string;
  uploadedBy: string;
}

export interface CompanyStatusData {
  id: string;
  status: string;
  companyName: string;
  companyCnpj: string;
  submittedAt?: string;
  approvedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  resubmissionCount: number;
  history: CompanyHistoryEntry[];
}

export interface CompanyHistoryEntry {
  id: string;
  fromStatus?: string;
  toStatus: string;
  action: string;
  changedBy: string;
  changedAt: string;
  notes?: string;
}

export interface CompanyAdminSummary {
  id: string;
  companyName: string;
  companyCnpj: string;
  status: string;
  createdAt: string;
  tenantId?: string;
  tenantName?: string;
}

export interface CompanyAdminDetail {
  id: string;
  tenantId: string;
  status: string;
  companyName: string;
  companyCnpj: string;
  companyType: string;
  tradeName: string;
  companyEmail: string;
  companyPhone: string;
  companyWebsite?: string;
  companyDescription: string;
  representativeName: string;
  representativeCpf: string;
  representativeDocType: string;
  representativeBirthDate: string;
  representativePhone: string;
  representativeMotherName: string;
  addressZipCode: string;
  addressStreet: string;
  addressNumber: string;
  addressNeighborhood: string;
  addressCity: string;
  addressState: string;
  addressComplement?: string;
  accountEmail: string;
  gatewayCompanyId?: string;
  submittedAt?: string;
  approvedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  resubmissionCount: number;
  createdAt: string;
  updatedAt: string;
  documents: CompanyDocument[];
  history: CompanyHistoryEntry[];
  tenant?: { id: string; name: string };
}

export interface CompanyQueryParams {
  status?: string;
  search?: string;
  tenantId?: string;
  page?: number;
  limit?: number;
}

// ============================================
// Company (Banking) Hooks
// ============================================

export function useCompanyStatus() {
  return useQuery({
    queryKey: ['company-status'],
    queryFn: async () => {
      const { data } = await api.get('/banking/company/status');
      return data as CompanyStatusData;
    },
  });
}

export function useCompanyDocuments() {
  return useQuery({
    queryKey: ['company-documents'],
    queryFn: async () => {
      const { data } = await api.get('/banking/company/documents');
      return data as CompanyDocument[];
    },
  });
}

export function useCreateCompanyRegistration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CompanyRegistrationData) => {
      const { data } = await api.post('/banking/company/register', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-status'] });
    },
  });
}

export function useUpdateCompanyRegistration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<CompanyRegistrationData>) => {
      const { data } = await api.put('/banking/company/register', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-status'] });
    },
  });
}

export function useUploadCompanyDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, documentType }: { file: File; documentType: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', documentType);
      const { data } = await api.post('/banking/company/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-documents'] });
      queryClient.invalidateQueries({ queryKey: ['company-status'] });
    },
  });
}

export function useSubmitCompanyRegistration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/banking/company/submit');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-status'] });
    },
  });
}

// ============================================
// Admin Hooks
// ============================================

export function useAdminCompanies(query: CompanyQueryParams = {}) {
  return useQuery({
    queryKey: ['admin-companies', query],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (query.status) params.append('status', query.status);
      if (query.search) params.append('search', query.search);
      if (query.page) params.append('page', String(query.page));
      if (query.limit) params.append('limit', String(query.limit));
      const { data } = await api.get(`/admin/companies?${params.toString()}`);
      return data as { data: CompanyAdminSummary[]; total: number; page: number; limit: number };
    },
  });
}

export function useAdminCompanyDetail(id: string) {
  return useQuery({
    queryKey: ['admin-company-detail', id],
    queryFn: async () => {
      const { data } = await api.get(`/admin/companies/${id}`);
      return data as CompanyAdminDetail;
    },
    enabled: !!id,
  });
}

export function useActivateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, gatewayCompanyId, notes }: { id: string; gatewayCompanyId: string; notes?: string }) => {
      const { data } = await api.post(`/admin/companies/${id}/activate`, { gatewayCompanyId, notes });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
      queryClient.invalidateQueries({ queryKey: ['admin-company-detail'] });
    },
  });
}

export function useRejectCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, rejectionReason }: { id: string; rejectionReason: string }) => {
      const { data } = await api.post(`/admin/companies/${id}/reject`, { rejectionReason });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
      queryClient.invalidateQueries({ queryKey: ['admin-company-detail'] });
    },
  });
}

// ============================================
// Super Admin Hooks
// ============================================

export function useSuperAdminCompanies(query: CompanyQueryParams = {}) {
  return useQuery({
    queryKey: ['super-admin-companies', query],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (query.status) params.append('status', query.status);
      if (query.search) params.append('search', query.search);
      if (query.tenantId) params.append('tenantId', query.tenantId);
      if (query.page) params.append('page', String(query.page));
      if (query.limit) params.append('limit', String(query.limit));
      const { data } = await api.get(`/super-admin/companies?${params.toString()}`);
      return data as { data: CompanyAdminSummary[]; total: number; page: number; limit: number };
    },
  });
}

export function useSuperAdminCompanyDetail(id: string) {
  return useQuery({
    queryKey: ['super-admin-company-detail', id],
    queryFn: async () => {
      const { data } = await api.get(`/super-admin/companies/${id}`);
      return data as CompanyAdminDetail;
    },
    enabled: !!id,
  });
}

export function useSuperAdminCompanyHistory(id: string) {
  return useQuery({
    queryKey: ['super-admin-company-history', id],
    queryFn: async () => {
      const { data } = await api.get(`/super-admin/companies/${id}/history`);
      return data as CompanyHistoryEntry[];
    },
    enabled: !!id,
  });
}
